// Pre-defined "groupings" for the Analytics page.
//
// Each preset auto-selects a fixed set of dimensions + metrics (and optionally
// default filters) and, while it is active, contributes its own criteria rows
// to the Summary card.
//
// A preset declares `criteria`: a list of rows shaped like
//   { label, segments: [{ key, tone, match }] }
// where `tone` is 'success' | 'warning' | 'danger' and `match(leafRow)` is a
// predicate. The page uses each predicate for BOTH the displayed count (number
// of matching leaf rows) and the click-to-filter drill-in, so they always agree.

// SumDuration is returned by the backend in SECONDS.
const toMinutes = (seconds) => (Number(seconds) || 0) / 60;
const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

// Leaf-row metric accessors used by the predicates below.
const count = (leaf) => Number(leaf?.metrics?.Count);
const minutes = (leaf) => toMinutes(leaf?.metrics?.SumDuration);
const videoPlusImage = (leaf) => num(leaf?.metrics?.VideoCount) + num(leaf?.metrics?.ImageCount);
// const videoPlusTotal = (leaf) => num(leaf?.metrics?.VideoCount) + num(leaf?.metrics?.TotalCount);
const totalCountOf = (leaf) => num(leaf?.metrics?.TotalCount);
const videoCountOf = (leaf) => num(leaf?.metrics?.VideoCount);

export const ANALYTICS_PRESETS = [
  {
    id: 'kazanimSure',
    label: 'Kazanım-Süre Analizi',
    reportType: 'topicStats',
    // Note: "SubCourseTopicName" is the display label for the `Name` dimension.
    // dimensions: ['SegmentCourseId', 'Name', 'AchievementId', 'AchievementVideoId'],
    dimensions: ['SegmentCourseId', 'Name', 'AchievementId'],
    metrics: ['Count', 'SumDuration'],
    criteriaTitle: 'Süre Kriterleri',
    criteria: [
      {
        label: 'Tek parttan Oluşan Kazanım Video Süresi · ideal = 2–10 min',
        segments: [
          { key: 'kazanimSure.c1.ok', tone: 'success', match: (l) => count(l) === 1 && minutes(l) >= 2 && minutes(l) <= 10 },
          { key: 'kazanimSure.c1.bad', tone: 'danger', match: (l) => count(l) === 1 && !(minutes(l) >= 2 && minutes(l) <= 10) },
        ],
      },
      {
        label: 'İki parttan Oluşan Kazanım Video Süresi · ideal = 8–17 min',
        segments: [
          { key: 'kazanimSure.c2.ok', tone: 'success', match: (l) => count(l) === 2 && minutes(l) >= 8 && minutes(l) <= 17 },
          { key: 'kazanimSure.c2.bad', tone: 'danger', match: (l) => count(l) === 2 && !(minutes(l) >= 8 && minutes(l) <= 17) },
        ],
      },
      {
        label: '2 den Fazla olan partlı Video Sayısı',
        segments: [
          { key: 'kazanimSure.c3', tone: 'danger', match: (l) => count(l) > 2 },
        ],
      },
    ],
  },

  {
    id: 'konuKazanim',
    label: 'Konu - Kazanım Analizi',
    reportType: 'topicStats',
    dimensions: ['SegmentCourseId', 'Name', 'RowStatus'],
    metrics: ['Count'],
    localFilters: { RowStatus: [true] },
    criteriaTitle: 'Kazanım Kriterleri',
    criteria: [
      {
        label: 'Konu Bazında Kazanım Sayısı · Geçerli değer =  3–7',
        segments: [
          { key: 'konuKazanim.ok', tone: 'success', match: (l) => count(l) >= 3 && count(l) <= 7 },
        ],
      },
      {
        label: 'Konu Bazında Kazanım Sayısı < 3',
        segments: [
          { key: 'konuKazanim.lt3', tone: 'danger', match: (l) => Number.isFinite(count(l)) && count(l) < 3 },
        ],
      },
      {
        label: 'Konu Bazında Kazanım Sayısı > 7',
        segments: [
          { key: 'konuKazanim.gt7', tone: 'danger', match: (l) => count(l) > 7 },
        ],
      },
    ],
  },

  {
    id: 'kazanimSoru',
    label: 'Kazanım Soru Sayısı',
    reportType: 'questionCount',
    dimensions: ['AchievementId', 'RowStatus'],
    metrics: ['TotalCount', 'VideoCount', 'ImageCount'],
    localFilters: { RowStatus: [true] },
    criteriaTitle: 'Soru Sayısı Kriterleri',
    criteria: [
      {
        label: 'Kazanım Soru Sayısı ≥ 8',
        segments: [
          { key: 'kazanimSoru.ok', tone: 'success', match: (l) => videoPlusImage(l) >= 8 },
        ],
      },
      {
        label: 'Kazanım Soru Sayısı = 5–7',
        segments: [
          { key: 'kazanimSoru.warn', tone: 'warning', match: (l) => videoPlusImage(l) >= 5 && videoPlusImage(l) < 8 },
        ],
      },
      {
        label: 'Kazanım Soru Sayısı < 5',
        segments: [
          { key: 'kazanimSoru.err', tone: 'danger', match: (l) => videoPlusImage(l) < 5 },
        ],
      },
    ],
  },

  {
    id: 'videoluSoru',
    label: 'Videolu Soru Sayısı',
    reportType: 'questionCount',
    dimensions: ['RowStatus'],
    metrics: ['TotalCount', 'VideoCount', 'ImageCount'],
    localFilters: { RowStatus: [true] },
    criteriaTitle: 'Soru Sayısı Kriterleri',
    criteria: [
      {
        section: 'Konu Bazında Toplam Soru Sayısı',
        label: '≥ 100',
        // label: 'Video Çözümlü Soru Sayısı ≥ 100',
        segments: [
          { key: 'videoluSoru.total.ok', tone: 'success', match: (l) => totalCountOf(l) >= 100 },
        ],
      },
      {
        // label: 'Video Çözümlü Soru Sayısı = 50–99',
        section: 'Konu Bazında Toplam Soru Sayısı',
        label: '50–99',
        segments: [
          { key: 'videoluSoru.total.warn', tone: 'warning', match: (l) => totalCountOf(l) >= 50 && totalCountOf(l) < 100 },
        ],
      },
      {
        // label: 'Video Çözümlü Soru Sayısı < 50',
        section: 'Konu Bazında Toplam Soru Sayısı',
        label: '< 50',
        segments: [
          { key: 'videoluSoru.total.err', tone: 'danger', match: (l) => totalCountOf(l) < 50 },
        ],
      },
      {
        section: 'Konu Bazında Video Çözümlü Soru Sayısı',
        label: '≥ 100',
        segments: [
          { key: 'videoluSoru.video.ok', tone: 'success', match: (l) => videoCountOf(l) >= 100 },
        ],
      },
      {
        section: 'Konu Bazında Video Çözümlü Soru Sayısı',
        label: '50–99',
        segments: [
          { key: 'videoluSoru.video.warn', tone: 'warning', match: (l) => videoCountOf(l) >= 50 && videoCountOf(l) < 100 },
        ],
      },
      {
        section: 'Konu Bazında Video Çözümlü Soru Sayısı',
        label: '< 50',
        segments: [
          { key: 'videoluSoru.video.err', tone: 'danger', match: (l) => videoCountOf(l) < 50 },
        ],
      },
    ],
  },
];

export const getPresetsForReport = (reportType) =>
  ANALYTICS_PRESETS.filter((preset) => preset.reportType === reportType);

export const getPresetById = (id) =>
  ANALYTICS_PRESETS.find((preset) => preset.id === id) || null;
